import { Injectable, Injector } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SupportErrorContext } from '../_models/support-error-context';
import { ErrorModalHelperService } from './error-modal-helper/error-modal-helper.service';
import { MonitoringService } from '../_services/monitoring/monitoring.service';
import { urlPath } from './describe-error';

/**
 * Marks a request whose caller handles its own failure, such as a filter lookup that shows an inline
 * "Couldn't load" note. Its failure raises no support modal; it is still logged to Application Insights.
 */
export const QUIET_ERRORS = new HttpContextToken<boolean>(() => false);

/** The HttpContext for a request whose caller handles its own failure. */
export function quietErrorsContext(): HttpContext {
  return new HttpContext().set(QUIET_ERRORS, true);
}

/**
 * Global HTTP error interceptor. On an unexpected request failure it captures the
 * diagnostic context (time, request, logged-in user, error message, requestId) and
 * shows the support modal, then rethrows so existing service-level error handling
 * (BaseService.handleError / per-component catchError) still runs.
 *
 * Skipped statuses:
 *  - 401: handled by UnauthorisedUserLogoutInterceptor (redirect to /login)
 *  - 422: business-validation failures (retire-299-wire-swap.md); the validation modal handles
 *    them, not the support modal
 *  - 413: a save too large to accept; BaseService.handleError turns it into a plain message (40759), which
 *    the support modal would otherwise block
 *
 * A request sent with quietErrorsContext() never shows the modal: its caller handles the failure, and the
 * interceptor logs it to Application Insights instead, so it is still seen (40760).
 *
 * MonitoringService is resolved lazily via Injector to avoid a DI cycle
 * (interceptor -> service -> HttpClient -> interceptors).
 */
@Injectable()
export class ErrorSupportInterceptor implements HttpInterceptor {

  constructor(
    private errorModalHelper: ErrorModalHelperService,
    private injector: Injector
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && this.shouldShow(error.status)) {
          if (request.context.get(QUIET_ERRORS)) {
            this.trackQuiet(request, error);
          } else {
            this.errorModalHelper.showSupport(this.buildContext(request, error));
          }
        }
        return throwError(() => error);
      })
    );
  }

  private shouldShow(status: number): boolean {
    return status !== 401 && status !== 413 && status !== 422;
  }

  /** A quiet request's failure: logged with the call (path only, never the query), and no modal. */
  private trackQuiet(request: HttpRequest<any>, error: HttpErrorResponse): void {
    const message = `${request.method} ${urlPath(request.url)} failed: ${error.status} ${error.statusText}`.trim();
    this.injector.get(MonitoringService, null)?.trackException(new Error(message));
  }

  private buildContext(request: HttpRequest<any>, error: HttpErrorResponse): SupportErrorContext {
    return {
      timestamp: new Date().toISOString(),
      method: request.method,
      url: request.urlWithParams,
      status: error.status,
      statusText: error.statusText,
      requestId: request.headers.get('RequestId'),
      correlationId: this.getCorrelationId(),
      user: this.getUser(),
      errorMessage: this.getErrorMessage(error)
    };
  }

  private getCorrelationId(): string | null {
    const monitoring = this.injector.get(MonitoringService, null);
    return monitoring?.getOperationId() ?? null;
  }

  /** Read the current user from localStorage — same source BaseService uses, avoids a DI cycle. */
  private getUser(): string {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw || raw === 'undefined' || raw === 'null') {
        return 'Not logged in';
      }
      const user = JSON.parse(raw);
      const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
      const parts = [name || user?.username, user?.email ? `(${user.email})` : null,
        user?.id != null ? `[#${user.id}]` : null].filter(Boolean);
      return parts.length ? parts.join(' ') : 'Not logged in';
    } catch {
      return 'Not logged in';
    }
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (typeof error.error.Message === 'string') {
        return error.error.Message;
      }
      if (typeof error.error.message === 'string') {
        return error.error.message;
      }
    }
    return error.message || 'An unknown error occurred.';
  }
}
